import React from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { motion } from 'motion/react';
import { duration } from './primitives/tokens';
import styles from './Skeleton.module.css';

const skeleton = cva(styles.base);

// ── Sub-components ───────────────────────────────────────────────────────────

interface SkeletonBaseProps {
  /** Animate the shimmer effect */
  active?: boolean;
  style?: React.CSSProperties;
}

function SkeletonAvatar({ active = true, size = 40, shape = 'circle', style }: SkeletonBaseProps & { size?: number; shape?: 'circle' | 'square' }) {
  return (
    <div
      className={clsx(styles.block, styles.avatar, active && styles.active, shape === 'circle' ? styles.avatarCircle : styles.avatarSquare)}
      style={{
        '--skeleton-size': `${size}px`,
        ...style,
      } as React.CSSProperties}
    />
  );
}

function SkeletonButton({ active = true, style }: SkeletonBaseProps) {
  return (
    <div
      className={clsx(styles.block, styles.button, active && styles.active)}
      style={style}
    />
  );
}

function SkeletonInput({ active = true, style }: SkeletonBaseProps) {
  return (
    <div
      className={clsx(styles.block, styles.input, active && styles.active)}
      style={style}
    />
  );
}

function SkeletonImage({ active = true, style }: SkeletonBaseProps) {
  return (
    <div
      className={clsx(styles.block, styles.image, active && styles.active)}
      style={style}
    />
  );
}

// ── Main Skeleton ────────────────────────────────────────────────────────────

type DivMotionSafeProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'onDrag' | 'onDragEnd' | 'onDragStart' | 'onAnimationStart'>;

interface SkeletonProps extends Omit<DivMotionSafeProps, 'title'> {
  /** Animate shimmer */
  active?: boolean;
  /** Show skeleton (when false, renders children) */
  loading?: boolean;
  /** Show title line */
  title?: boolean | { width?: string | number };
  /** Show paragraph lines */
  paragraph?: boolean | { rows?: number; width?: (string | number)[] | string | number };
  /** Show avatar */
  avatar?: boolean | { size?: number; shape?: 'circle' | 'square' };
  children?: React.ReactNode;
}

/**
 * Skeleton — content loading placeholder.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | active | boolean | true | Shimmer animation |
 * | loading | boolean | true | Show skeleton (false = show children) |
 * | title | boolean \| { width } | true | Title line |
 * | paragraph | boolean \| { rows, width } | true (3 rows) | Paragraph lines |
 * | avatar | boolean \| { size, shape } | false | Avatar circle/square |
 *
 * Sub-components: `Skeleton.Avatar`, `Skeleton.Button`, `Skeleton.Input`, `Skeleton.Image`
 *
 * @example
 * <Skeleton loading={isLoading}>
 *   <Card>Actual content</Card>
 * </Skeleton>
 *
 * <Skeleton avatar paragraph={{ rows: 2 }} />
 * <Skeleton.Input active />
 */
export function Skeleton({
  active = true,
  loading = true,
  title: titleProp = true,
  paragraph: paragraphProp = true,
  avatar: avatarProp = false,
  children,
  style,
  className,
  ...props
}: SkeletonProps) {
  const safeProps = props;
  if (!loading) return <>{children}</>;

  const showTitle = !!titleProp;
  const titleWidth = typeof titleProp === 'object' ? (titleProp.width ?? '40%') : '40%';

  const showParagraph = !!paragraphProp;
  const rows = typeof paragraphProp === 'object' ? (paragraphProp.rows ?? 3) : 3;
  const paragraphWidths =
    typeof paragraphProp === 'object' && Array.isArray(paragraphProp.width) ? paragraphProp.width : undefined;
  const defaultLastWidth =
    typeof paragraphProp === 'object' && typeof paragraphProp.width === 'string' ? paragraphProp.width : '60%';

  const showAvatar = !!avatarProp;
  const avatarSize = typeof avatarProp === 'object' ? (avatarProp.size ?? 40) : 40;
  const avatarShape = typeof avatarProp === 'object' ? (avatarProp.shape ?? 'circle') : 'circle';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: duration.emphasis / 1000 }}
      className={skeleton({ className })}
      style={style}
      {...safeProps}
    >
      {showAvatar && (
        <div
          className={clsx(styles.block, styles.avatar, active && styles.active, avatarShape === 'circle' ? styles.avatarCircle : styles.avatarSquare)}
          style={{ '--skeleton-size': `${avatarSize}px` } as React.CSSProperties}
        />
      )}

      <div className={styles.contentCol}>
        {showTitle && (
          <div
            className={clsx(styles.block, styles.titleLine, active && styles.active)}
            style={{ '--skeleton-width': typeof titleWidth === 'number' ? `${titleWidth}px` : titleWidth } as React.CSSProperties}
          />
        )}
        {showParagraph &&
          Array.from({ length: rows }, (_, i) => {
            const isLast = i === rows - 1;
            const w = paragraphWidths?.[i] ?? (isLast ? defaultLastWidth : '100%');
            return (
              <div
                key={i}
                className={clsx(styles.block, styles.paragraphLine, active && styles.active)}
                style={{ '--skeleton-width': typeof w === 'number' ? `${w}px` : w } as React.CSSProperties}
              />
            );
          })}
      </div>
    </motion.div>
  );
}

// Attach sub-components
Skeleton.Avatar = SkeletonAvatar;
Skeleton.Button = SkeletonButton;
Skeleton.Input = SkeletonInput;
Skeleton.Image = SkeletonImage;
